import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewChild
} from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { BaseAbstractPage, GeolocationService } from '@incust-mobile/core';
import { POSApiService, PosApiModel } from '@incust-mobile/api';
import { GoogleMap, Marker } from '@capacitor/google-maps';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-pos-map-page',
  templateUrl: 'pos-map.page.html',
  styleUrls: ['pos-map.page.scss'],
  standalone: true,
  imports: [IonicModule, TranslatePipe]
})
export class PosMapPage extends BaseAbstractPage implements AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: true }) mapRef!: ElementRef<HTMLElement>;

  private posApi = inject(POSApiService);
  private geo = inject(GeolocationService);

  isLoading = signal(true);
  selectedPos = signal<PosApiModel | null>(null);

  private map: GoogleMap | null = null;

  constructor() {
    super();
  }

  async ngAfterViewInit() {
    await this.geo.checkPermission();
    await this.initMap();
    await this.loadAndPin();
  }

  ngOnDestroy() {
    this.map?.destroy();
  }

  goToList() {
    this.router.navigate(['/home/pos-list']);
  }

  goToDetail(id: string) {
    this.router.navigate(['/home/pos-detail', id]);
  }

  closeCard() {
    this.selectedPos.set(null);
  }

  private async initMap() {
    const coords = this.geo.coords();
    const center = coords
      ? { lat: coords.lat, lng: coords.lng }
      : { lat: 50.45, lng: 30.52 }; // Kyiv fallback

    this.map = await GoogleMap.create({
      id: 'pos-map',
      element: this.mapRef.nativeElement,
      apiKey: environment.googleMapsApiKey,
      config: {
        center,
        zoom: 12
      }
    });

    await this.map.setOnMarkerClickListener((marker) => {
      const pos = this.posItems.find((p) => p.id === marker.title);
      if (pos) {
        this.selectedPos.set(pos);
      }
    });
  }

  private posItems: PosApiModel[] = [];

  private async loadAndPin() {
    this.isLoading.set(true);
    const coords = this.geo.coords();

    try {
      const items = await firstValueFrom(
        this.posApi.incustControllersClientClientPos(
          undefined,
          coords?.lat,
          coords?.lng,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          1,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          500,
          0
        )
      );

      this.posItems = items ?? [];
      await this.addMarkers(this.posItems);
    } catch {
      await this.notificationService.show(
        'app#city_card#pos_map#load_error',
        2000,
        'top',
        'danger'
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  private async addMarkers(items: PosApiModel[]) {
    if (!this.map || !items.length) return;

    const markers: Marker[] = items
      .filter((p) => p.latitude != null && p.longitude != null)
      .map((p) => ({
        coordinate: { lat: p.latitude!, lng: p.longitude! },
        title: p.id ?? '',
        snippet: p.addresses ?? ''
      }));

    await this.map.addMarkers(markers);
  }
}
